import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Listings.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

function StatusPill({ status, onClick }) {
  const cls =
    status === "Active" ? "badge green as-button"
      : (status || "").toLowerCase() === "pending" ? "badge amber as-button"
        : "badge gray as-button";
  const label = status === "Active" ? "Active" : "Pending";
  return (
    <button className={cls} onClick={onClick} title="Click to toggle Pending/Active">
      {label}
    </button>
  );
}

export default function GuideListings({ onStatusChange }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [guides, setGuides] = useState([]);

  // filters + statuses (new)
  const [filters, setFilters] = useState({ search: "", status: "" });
  const [statuses, setStatuses] = useState([]);

  useEffect(() => {
    let mounted = true;
    async function loadGuides() {
      try {
        setLoading(true);
        setErr("");
        const url = `${API}/admin/listings/guides`;
        const res = await fetch(url);
        const json = await res.json();
        if (!res.ok) throw new Error(json?.message || "Failed to fetch guide listings.");
        if (mounted) {
          setGuides(Array.isArray(json) ? json : []);
        }
      } catch (e) {
        if (mounted) setErr(e.message || "Load failed");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    async function loadStatuses() {
      try {
        const r = await fetch(`${API}/statuses`);
        const j = await r.json();
        if (r.ok && Array.isArray(j)) setStatuses(j);
      } catch (e) {
        /* ignore */
      }
    }

    loadGuides();
    loadStatuses();
    return () => { mounted = false; };
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ search: "", status: "" });
  };

  // client-side filtered list
  const filtered = useMemo(() => {
    const s = (filters.search || "").toLowerCase().trim();
    const st = (filters.status || "").toLowerCase().trim();
    return guides.filter(g => {
      if (st && (String(g.status || "").toLowerCase() !== st)) return false;
      if (!s) return true;
      // search over listId (tourCode), guideName, tourTitle, serviceArea, languages
      const hay = [
        g.listId,
        g.tourCode,
        g.guideName,
        g.tourTitle,
        g.serviceArea,
        g.languages
      ].join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [guides, filters]);

  async function toggleStatusGuide(tourId) {
    try {
      // optimistic update
      setGuides((prev) =>
        prev.map((g) =>
          g.tourId === tourId
            ? { ...g, status: g.status === "Active" ? "pending" : "Active" }
            : g
        )
      );

      const res = await fetch(`${API}/admin/listings/guides/${tourId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}) // keep body empty but send JSON for compatibility
      });

      const data = await res.json().catch(() => ({}));

      // If server responded with a fresh row, update that single row and summary
      if (data && data.row) {
        setGuides((prev) =>
          prev.map((g) => (g.tourId === tourId ? { ...g, ...data.row } : g))
        );
        if (onStatusChange && data.summary) onStatusChange(data.summary);
        return;
      }

      // If response was OK but no row provided, just refetch the list to be safe
      if (res.ok) {
        const r2 = await fetch(`${API}/admin/listings/guides`);
        const j2 = await r2.json();
        if (r2.ok) setGuides(Array.isArray(j2) ? j2 : []);
        return;
      }

      // If server returned error (but DB might have changed), attempt to refresh silently
      const r3 = await fetch(`${API}/admin/listings/guides`);
      const j3 = await r3.json();
      if (r3.ok) setGuides(Array.isArray(j3) ? j3 : []);

      // log server message instead of alerting user (prevents false "Failed..." when DB updated)
      console.error("Guide status update response:", data?.message || res.statusText);
    } catch (e) {
      // network/error -> show alert and refetch to restore state
      alert(e.message || "Update failed.");
      const res = await fetch(`${API}/admin/listings/guides`);
      const json = await res.json();
      if (res.ok) setGuides(Array.isArray(json) ? json : []);
    }
  }

  return (
    <section className={`card table-card ${loading ? 'loading' : ''}`}>
      <div className="table-head">
        <h3 className="table-title">Guide Listings</h3>
      </div>

      <div className="filters" style={{ marginTop: 8 }}>
        <input
          type="text"
          name="search"
          placeholder="Search by ID, Title, Guide, Area, Language"
          value={filters.search}
          onChange={handleFilterChange}
        />
        <select
          name="status"
          value={filters.status}
          onChange={handleFilterChange}
          className={`input ${filters.status ? "filter-selected" : "filter-muted"}`}
        >
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
        </select>
        <button onClick={handleClearFilters}>Clear All</button>
      </div>

      <br />
      {err && <div style={{ color: "crimson", padding: '10px 20px' }}>{err}</div>}
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 34 }}><input type="checkbox" aria-label="select all" /></th>
            <th>Guide ID</th>
            <th>Guide Name</th>
            <th>Tour Title</th>
            <th>Tour Type</th>
            <th>Service Area</th>
            <th>Languages</th>
            <th>Contact info</th>
            <th>Description</th>
            <th style={{ width: 120 }}>Status</th>
            <th style={{ width: 100 }}>Hold</th>
            <th style={{ width: 120 }}>Details</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((g, index) => (
            <tr key={`${g.tourId}-${index}`}>
              <td><input type="checkbox" aria-label={`select ${g.listId}`} /></td>
              <td><span className="id-badge">{g.listId}</span></td>
              <td>{g.guideName}</td>
              <td className="muted">{g.tourTitle}</td>
              <td className="muted">{g.tourType}</td>
              <td className="muted">{g.serviceArea}</td>
              <td className="muted">{g.languages}</td>
              <td className="muted">
                <div className="mono">{g.contact?.phone || "—"}</div>
                <div className="mono">{g.contact?.email || "—"}</div>
              </td>
              <td className="muted clamp-2">{g.description}</td>
              <td>
                <StatusPill status={g.status} onClick={() => toggleStatusGuide(g.tourId)} />
              </td>
              <td>
                <button
                  className={`badge ${g.status === "Active" ? "amber" : "green"} as-button`}
                  onClick={() => toggleStatusGuide(g.tourId)}
                >
                  {g.status === "Active" ? "Hold" : "Unhold"}
                </button>
              </td>
              <td>
                <button
                  className="badge gray as-button"
                  onClick={() => {
                    if (g.tourId) {
                      navigate(`/admin/listings/guides/${g.tourId}`);
                    } else {
                      alert("Tour details not found.");
                    }
                  }}
                >
                  View Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
